import { useRef, useState } from "react";
import { Alert, Box, InputBase, MenuItem, Select, Snackbar, Typography, useMediaQuery } from "@mui/material";

import nominateTemple from "../../../assets/Images/Temples/nominate-temple.svg";
import chevronDown from "../../../assets/Images/Temples/chevron-down.svg";
import uploadImage from "../../../assets/Images/Temples/upload-image.svg";
import { INDIA_STATES } from "../../../utils/indiaStates";
import { sanitizeFormFieldValue, validateIndianMobile } from "../../../utils/phoneValidation";
import { SectionTag, TEMPLE_COLORS, TempleButton } from "../shared";

const MAX_PHOTOS = 5;

const initialForm = {
  your_name: "",
  phone_number: "",
  location: "",
  state: "",
  temple_condition: "",
};

const inputSx = (hasError, m) => ({
  width: "100%",
  minHeight: m ? 46 : 48,
  px: m ? "14px" : "16px",
  background: "#FFFFFF",
  border: `1px solid ${hasError ? TEMPLE_COLORS.red : "#E3E3E3"}`,
  borderRadius: "10px",
  fontFamily: "'Red Hat Display', sans-serif",
  fontSize: "14px",
  lineHeight: "20px",
  color: TEMPLE_COLORS.ink,
  "&.Mui-focused": { borderColor: TEMPLE_COLORS.red },
  "& input::placeholder, & textarea::placeholder": { color: "#9A9A9A", opacity: 1 },
});

const Field = ({ label, error, m, children }) => (
  <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "8px" }}>
    <Typography component="label" sx={{ fontWeight: 500, fontSize: m ? "13px" : "14px", lineHeight: "18px", color: TEMPLE_COLORS.ink }}>
      {label}
    </Typography>
    {children}
    {error && <Typography sx={{ fontSize: "12px", lineHeight: "16px", color: TEMPLE_COLORS.red }}>{error}</Typography>}
  </Box>
);

const ChevronIcon = (props) => (
  <Box component="img" src={chevronDown} alt="" {...props} sx={{ width: 16, height: 16, right: "16px !important", top: "calc(50% - 8px) !important" }} />
);

// `onSubmit(fields, photos)` posts to /temple-nominations.
const NominateTempleSection = ({ content, onSubmit }) => {
  const isTablet = useMediaQuery("(max-width: 1199px)");
  const isMobile = useMediaQuery("(max-width: 599px)");
  const fileInputRef = useRef(null);

  const [form, setForm] = useState(initialForm);
  const [photos, setPhotos] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const handleChange = (key) => (e) => {
    const value = sanitizeFormFieldValue(key, e?.target?.value ?? "");
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handlePhotos = (e) => {
    const picked = Array.from(e.target.files || []).filter((file) => /^image\/(jpeg|png)$/.test(file.type));
    setPhotos((prev) => [...prev, ...picked].slice(0, MAX_PHOTOS));
    e.target.value = "";
  };

  const validate = () => {
    const next = {};
    if (!form.your_name.trim()) next.your_name = "Enter your name";
    const phone = validateIndianMobile(form.phone_number);
    if (!phone.isValid) next.phone_number = phone.error;
    if (!form.location.trim()) next.location = "Enter the village or city";
    if (!form.state) next.state = "Select a state";
    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSubmitting(true);
    try {
      await onSubmit({ ...form, phone_number: validateIndianMobile(form.phone_number).digits }, photos);
      setSnackbar({ open: true, severity: "success", message: "Temple details submitted. Our team will contact you soon." });
      setForm(initialForm);
      setPhotos([]);
    } catch (err) {
      setSnackbar({ open: true, severity: "error", message: err?.message || "Couldn't submit temple details. Try again." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        overflow: "hidden",
        background: "#FFF6F5",
        px: isMobile ? "20px" : isTablet ? "40px" : "120px",
        py: isMobile ? "40px" : "100px",
      }}
    >
      {isMobile && (
        <Box
          component="img"
          src={nominateTemple}
          alt=""
          sx={{ position: "absolute", right: 5, top: 27.3, width: 102.6, height: 126.4, pointerEvents: "none" }}
        />
      )}
      {!isTablet && (
        <Box
          component="img"
          src={nominateTemple}
          alt=""
          sx={{ position: "absolute", left: "calc(50% - 250px)", top: 542.6, width: 251.98, height: 310.57, pointerEvents: "none" }}
        />
      )}

      <Box
        sx={{
          position: "relative",
          maxWidth: 1200,
          mx: "auto",
          display: "flex",
          flexDirection: isTablet ? "column" : "row",
          alignItems: isTablet ? "stretch" : "center",
          gap: isMobile ? "24px" : isTablet ? "40px" : "72px",
        }}
      >
        {/* Intro */}
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "flex-start", gap: isMobile ? "14px" : "22px" }}>
          <SectionTag small={isMobile}>{content.tag}</SectionTag>
          <Typography
            component="h2"
            sx={{ fontWeight: 700, fontSize: isMobile ? "28px" : "44px", lineHeight: isMobile ? "36px" : "54px", color: TEMPLE_COLORS.ink }}
          >
            {content.titleLine1}
            <Box component="span" sx={{ display: "block", color: TEMPLE_COLORS.red }}>
              {content.titleLine2}
            </Box>
          </Typography>
          <Typography sx={{ fontSize: isMobile ? "15px" : "17px", lineHeight: isMobile ? "24px" : "29px", color: TEMPLE_COLORS.body }}>
            {content.description}
          </Typography>
          <Box sx={{ width: "100%", pt: isMobile ? "4px" : "6px", display: "flex", flexDirection: "column", gap: isMobile ? "12px" : "16px" }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: isMobile ? "11px" : "13px",
                lineHeight: isMobile ? "14px" : "16px",
                letterSpacing: "2px",
                textTransform: "uppercase",
                color: TEMPLE_COLORS.red,
              }}
            >
              {content.stepsLabel}
            </Typography>
            {content.steps.map((step, index) => (
              <Box key={step} sx={{ display: "flex", alignItems: "center", gap: isMobile ? "12px" : "14px" }}>
                <Box
                  sx={{
                    width: isMobile ? 28 : 34,
                    height: isMobile ? 28 : 34,
                    flexShrink: 0,
                    borderRadius: isMobile ? "14px" : "17px",
                    background: "#FFFFFF",
                    border: "1px solid rgba(240, 73, 81, 0.35)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: isMobile ? "12px" : "14px", lineHeight: isMobile ? "14px" : "18px", color: TEMPLE_COLORS.red }}>
                    {index + 1}
                  </Typography>
                </Box>
                <Typography sx={{ fontWeight: 500, fontSize: isMobile ? "14px" : "15px", lineHeight: isMobile ? "20px" : "22px", color: TEMPLE_COLORS.ink }}>
                  {step}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Lead form */}
        <Box
          component="form"
          noValidate
          onSubmit={handleSubmit}
          sx={{
            width: isTablet ? "100%" : 600,
            flexShrink: 0,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            gap: isMobile ? "16px" : "18px",
            px: isMobile ? "20px" : "36px",
            py: isMobile ? "24px" : "36px",
            background: "#FFFFFF",
            border: "1px solid #F3E1DF",
            borderRadius: isMobile ? "16px" : "20px",
            boxShadow: isMobile ? "0px 12px 30px 0px rgba(128, 38, 38, 0.06)" : "0px 16px 40px 0px rgba(128, 38, 38, 0.06)",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <Typography component="h3" sx={{ fontWeight: 700, fontSize: isMobile ? "20px" : "22px", lineHeight: isMobile ? "28px" : "30px", color: TEMPLE_COLORS.ink }}>
              {content.formTitle}
            </Typography>
            <Typography sx={{ fontSize: isMobile ? "13px" : "14px", lineHeight: isMobile ? "18px" : "20px", color: TEMPLE_COLORS.muted }}>
              {content.formSubtitle}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: "16px" }}>
            <Field label="Your Name *" error={errors.your_name} m={isMobile}>
              <InputBase
                value={form.your_name}
                onChange={handleChange("your_name")}
                placeholder="Enter your full name"
                inputProps={{ "aria-label": "Your name" }}
                sx={inputSx(errors.your_name, isMobile)}
              />
            </Field>
            <Field label="Phone Number *" error={errors.phone_number} m={isMobile}>
              <InputBase
                value={form.phone_number}
                onChange={handleChange("phone_number")}
                placeholder="Enter mobile number"
                inputMode="numeric"
                startAdornment={<Typography sx={{ fontSize: "14px", color: "#9A9A9A", mr: "8px" }}>+91</Typography>}
                inputProps={{ "aria-label": "Phone number" }}
                sx={inputSx(errors.phone_number, isMobile)}
              />
            </Field>
          </Box>

          <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: "16px" }}>
            <Field label="Village / City *" error={errors.location} m={isMobile}>
              <InputBase
                value={form.location}
                onChange={handleChange("location")}
                placeholder="Where is it located?"
                inputProps={{ "aria-label": "Village or city" }}
                sx={inputSx(errors.location, isMobile)}
              />
            </Field>
            <Field label="State *" error={errors.state} m={isMobile}>
              <Select
                value={form.state}
                onChange={handleChange("state")}
                displayEmpty
                input={<InputBase sx={inputSx(errors.state, isMobile)} />}
                IconComponent={ChevronIcon}
                inputProps={{ "aria-label": "State" }}
                renderValue={(value) => value || <Box component="span" sx={{ color: "#9A9A9A" }}>Select state</Box>}
                MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
              >
                {INDIA_STATES.map((state) => (
                  <MenuItem key={state} value={state}>
                    {state}
                  </MenuItem>
                ))}
              </Select>
            </Field>
          </Box>

          <Field label="Describe The Temple's Condition" m={isMobile}>
            <InputBase
              value={form.temple_condition}
              onChange={handleChange("temple_condition")}
              placeholder="E.g. roof leaking, walls cracked, temple closed since…"
              multiline
              minRows={3}
              inputProps={{ "aria-label": "Describe the temple's condition" }}
              sx={{ ...inputSx(false, isMobile), minHeight: isMobile ? 90 : 96, alignItems: "flex-start", py: isMobile ? "12px" : "14px" }}
            />
          </Field>

          <Box
            component="button"
            type="button"
            onClick={() => fileInputRef.current?.click()}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              p: isMobile ? "14px" : "16px",
              textAlign: "left",
              cursor: "pointer",
              background: "#FDF7F7",
              border: "1.2px dashed rgba(240, 73, 81, 0.5)",
              borderRadius: "10px",
              fontFamily: "inherit",
              "&:focus-visible": { outline: `2px solid ${TEMPLE_COLORS.ink}`, outlineOffset: "2px" },
            }}
          >
            <Box sx={{ width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Box component="img" src={uploadImage} alt="" />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, fontSize: isMobile ? "13px" : "14px", lineHeight: isMobile ? "18px" : "20px", color: TEMPLE_COLORS.ink }}>
                {photos.length > 0 ? `${photos.length} of ${MAX_PHOTOS} photos added` : "Upload temple photos"}
              </Typography>
              <Typography
                sx={{
                  fontSize: isMobile ? "12px" : "13px",
                  lineHeight: isMobile ? "16px" : "18px",
                  color: TEMPLE_COLORS.muted,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {photos.length > 0 ? photos.map((file) => file.name).join(", ") : `Up to ${MAX_PHOTOS} images · JPG or PNG`}
              </Typography>
            </Box>
          </Box>
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png" multiple hidden onChange={handlePhotos} />

          <TempleButton
            type="submit"
            disabled={submitting}
            sx={isMobile ? { width: "100%", fontSize: "15px", py: "15px", px: "24px", boxShadow: "none" } : { width: "100%" }}
          >
            {submitting ? "Submitting…" : content.submitLabel}
          </TempleButton>

          {/* The mobile design ends the form at the button. */}
          {!isMobile && (
            <Typography sx={{ fontSize: "12px", lineHeight: "18px", color: "#9A9A9A", whiteSpace: "pre-wrap" }}>{`🔒  ${content.privacyNote}`}</Typography>
          )}
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NominateTempleSection;
